// SPDX-License-Identifier: Apache-2.0

// TestConnectionButton (Iris).
//
// Per-card re-validation surface for the comms tab (Slack + Gmail). Calls
// POST /api/integrations/test-connection?which=<which> — read-only ping that
// the orchestrator never touches. Result is rendered inline next to the
// button:
//   - pending: spinner glyph + "testing…"
//   - ok:      green dot + "Connected as <team> / <email>"
//   - error:   red dot + truncated error message (200 chars max)
//
// Slack tokens (xoxc/xoxd cookies) expire when the user logs out of the
// Slack web app. Gmail OAuth tokens revoke when the user revokes app access
// in their Google account. Without this button the SPA's only signal that a
// connection is dead is "the next mid-conversation tool call fails" — too
// late, too noisy. The button surfaces the rot at-rest.
//
// Caching: a successful or failed test result lives in component state with
// a 60s TTL. Repeated clicks within the TTL replay the cached result so the
// user can't accidentally rate-limit Slack by mashing the button. Tokens
// themselves NEVER touch component state — only the test outcome.
//
// Accessibility: aria-live="polite" on the result so screen readers
// announce state changes; the button itself is a plain <button> with
// disabled state during the in-flight fetch.

import { Show, createSignal, type JSX } from "solid-js";

// Relative base — Vite proxy handles dev, same-origin handles prod.
const MCP_BASE = "/api/integrations";
const CACHE_TTL_MS = 60_000;
const ERROR_MAX_LEN = 200;

type Which = "slack" | "gmail";

interface TestOk {
  ok: true;
  team?: string;
  user?: string;
  email?: string;
}

interface TestErr {
  ok: false;
  error: string;
}

type TestResult = TestOk | TestErr;

interface CachedResult {
  result: TestResult;
  at: number;
}

export interface TestConnectionButtonProps {
  which: Which;
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, Math.max(0, n - 1)) + "…";
}

export default function TestConnectionButton(
  props: TestConnectionButtonProps
): JSX.Element {
  const [pending, setPending] = createSignal(false);
  const [result, setResult] = createSignal<TestResult | null>(null);
  // Component-local cache. Per design — never to localStorage.
  let cached: CachedResult | null = null;

  const onClick = async () => {
    if (pending()) return;
    const now = Date.now();
    if (cached && now - cached.at < CACHE_TTL_MS) {
      setResult(cached.result);
      return;
    }
    setPending(true);
    setResult(null);
    try {
      const res = await fetch(
        `${MCP_BASE}/test-connection?which=${props.which}`,
        { method: "POST" }
      );
      // The endpoint always returns 200 on a well-formed request; 4xx only
      // for malformed shape — surface those as errors too.
      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        parsed = { ok: false, error: `non-JSON response (status ${res.status})` };
      }
      const body =
        parsed && typeof parsed === "object" && "ok" in parsed
          ? (parsed as TestResult)
          : ({ ok: false, error: "unexpected response shape" } as TestErr);
      cached = { result: body, at: Date.now() };
      setResult(body);
    } catch (e) {
      const fallback: TestErr = {
        ok: false,
        error: `network error: ${(e as Error).message}`,
      };
      cached = { result: fallback, at: Date.now() };
      setResult(fallback);
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      class="iris-test-connection"
      data-testid={`iris-test-connection-${props.which}`}
    >
      <button
        type="button"
        class="hestia-settings__ghost iris-test-connection__btn"
        data-testid={`iris-test-connection-btn-${props.which}`}
        onClick={() => void onClick()}
        disabled={pending()}
      >
        {pending() ? "testing…" : "test connection"}
      </button>

      <span
        class="iris-test-connection__result"
        aria-live="polite"
        data-testid={`iris-test-connection-result-${props.which}`}
      >
        <Show when={result()}>
          {(r) => (
            <Show
              when={r().ok}
              fallback={
                <span
                  class="iris-test-connection__row iris-test-connection__row--err"
                  data-testid={`iris-test-connection-err-${props.which}`}
                >
                  <span class="iris-test-connection__dot iris-test-connection__dot--err" />
                  <span class="iris-test-connection__msg">
                    {truncate((r() as TestErr).error ?? "error", ERROR_MAX_LEN)}
                  </span>
                </span>
              }
            >
              <span
                class="iris-test-connection__row iris-test-connection__row--ok"
                data-testid={`iris-test-connection-ok-${props.which}`}
              >
                <span class="iris-test-connection__dot iris-test-connection__dot--ok" />
                <Show
                  when={props.which === "slack"}
                  fallback={
                    <span class="iris-test-connection__msg">
                      Connected as{" "}
                      <code>{(r() as TestOk).email ?? "(unknown)"}</code>
                    </span>
                  }
                >
                  <span class="iris-test-connection__msg">
                    Connected as{" "}
                    <code>{(r() as TestOk).team ?? "(unknown)"}</code>
                    <Show when={(r() as TestOk).user}>
                      {" "}
                      / <code>{(r() as TestOk).user}</code>
                    </Show>
                  </span>
                </Show>
              </span>
            </Show>
          )}
        </Show>
      </span>
    </div>
  );
}
